import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { getErrorMessage, getTrpcErrorCode } from '@/shared/lib';
import { Button } from '@/shared/ui';

export interface QuoteRequestUnavailableProps {
  /** 요청 정보 조회(quotes.getRequestForm) 실패. */
  readonly error: unknown;
  readonly onRetry: () => void;
  /** 인증 전 계정의 거절(FORBIDDEN)이면 온보딩 주소 — 다시 시도 대신 인증을 마치러 가는 링크를 보인다. */
  readonly onboardingHref?: string | null;
  /** 요청 불가(BAD_REQUEST) 안내의 제목 — 디자인 견적이 기본값이고, 일반 견적 화면은 상품 기준 문구를 넘긴다. */
  readonly heading?: string;
}

const FALLBACK_MESSAGE = '견적 요청 정보를 불러오지 못했습니다.';

/** 머리 바로 아래 가운데 안내 — 머리의 얇은 아랫선과 겹치지 않게 아랫선만 둔다. */
const BLOCK_CLASS =
  'flex flex-col items-center gap-4 border-b border-foreground/10 py-16 text-center';

/**
 * 견적 요청 폼 대신 보여 주는 안내(견적 설계 §5-2·§15).
 * - 요청 불가(BAD_REQUEST): 서버가 사유별 문구(상품 비공개·색상 공급 불가 등)를 메시지로 보낸다 — 다시 시도해도 같으므로 버튼이 없다.
 * - 준비 중(PRECONDITION_FAILED, Phase 6 gate): 기능이 열리기 전까지 같은 결과라 버튼이 없다.
 * - 거절(FORBIDDEN): 인증 전 계정(activeProcedure)이 대부분이다 — 다시 시도해도 같으므로 온보딩 링크만 둔다.
 * - 그 밖의 일시 오류만 다시 시도를 둔다.
 */
export const QuoteRequestUnavailable = ({
  error,
  onRetry,
  onboardingHref = null,
  heading = '이 디자인으로는 견적을 요청할 수 없습니다.',
}: QuoteRequestUnavailableProps): ReactElement => {
  const code = getTrpcErrorCode(error);
  const message = getErrorMessage(error, FALLBACK_MESSAGE);

  if (code === 'FORBIDDEN') {
    return (
      <div role="alert" className={BLOCK_CLASS}>
        <p className="text-sm text-destructive">{message}</p>
        {onboardingHref !== null && (
          <Link
            to={onboardingHref}
            className="text-sm font-semibold underline underline-offset-4 hover:no-underline"
          >
            인증 완료하러 가기
          </Link>
        )}
      </div>
    );
  }

  if (code === 'BAD_REQUEST') {
    return (
      <div role="alert" className={BLOCK_CLASS}>
        <p className="text-lg font-bold tracking-[-0.02em]">{heading}</p>
        <p className="max-w-xl text-sm text-destructive">{message}</p>
      </div>
    );
  }

  if (code === 'PRECONDITION_FAILED') {
    return (
      <div className={BLOCK_CLASS}>
        <p role="alert" className="text-sm text-muted-foreground">
          {message}
        </p>
      </div>
    );
  }

  return (
    <div role="alert" className={BLOCK_CLASS}>
      <p className="text-sm text-destructive">{message}</p>
      <Button variant="outline" className="h-11 border-foreground/20 px-6" onClick={onRetry}>
        다시 시도
      </Button>
    </div>
  );
};
